import { memo, useCallback, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

import { useTrackAgentPillClicked } from '@air/analytics';
import { BoardIcon } from '@air/design-icons';
import { reportErrorToBugsnag } from '@air/utils-error';

import { WorkspaceAgentPendingPill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPendingPill';
import { WorkspaceAgentPillThumbnail } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPillThumbnail';
import { WorkspaceAgentPreviewCardCloseButton } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPreviewCardCloseButton';
import { WorkspaceAgentReferencePill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentReferencePill';
import { WorkspaceAgentUnavailablePill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentUnavailablePill';
import { WorkspaceAgentBoardPreviewCard } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentBoardPreviewCard';
import { useOpenWorkspaceAgentBoardPill } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useOpenWorkspaceAgentBoardPill';
import { useWorkspaceAgentPreviewCardDisclosure } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentPreviewCardDisclosure';
import { usePrivateWorkspaceAgentPillBoard } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentPillBoard';
import { type WorkspaceAgentSingleBoardPill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import { isWorkspaceAgentPillBoardUnresolvableError } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentPillBoardUnresolvableError';
import { getWorkspaceAgentBoardLocation } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentLocationLabels';

export type PrivateWorkspaceAgentBoardPillChipProps = {
  pill: WorkspaceAgentSingleBoardPill;
};

/** One Bugsnag report per board per tab: re-mentions and re-scrolls are not new failures. */
const reportedBoardIds = new Set<string>();

const reportPillBoardErrorOnce = (boardId: string, error: unknown) => {
  if (reportedBoardIds.has(boardId)) {
    return;
  }

  reportedBoardIds.add(boardId);
  reportErrorToBugsnag({ error, context: 'Workspace agent board pill could not resolve its board' });
};

export const PrivateWorkspaceAgentBoardPillChip = memo(({ pill }: PrivateWorkspaceAgentBoardPillChipProps) => {
  const { boardId } = pill;
  const { openBoardPill } = useOpenWorkspaceAgentBoardPill();
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  /** The history surface has no editor to return to, so the chip itself is where the card came from. */
  const focusTrigger = useCallback(() => {
    triggerRef.current?.focus();
  }, []);

  const { inView, ref } = useInView({ triggerOnce: true });

  const { data: board, error, isLoading } = usePrivateWorkspaceAgentPillBoard({ boardId }, { enabled: inView });
  const { trackAgentPillClicked } = useTrackAgentPillClicked();
  const handleCardOpen = useCallback(() => {
    if (!board) {
      return;
    }

    trackAgentPillClicked({ object: 'board', object_count: 1, object_name: board.title });
  }, [board, trackAgentPillClicked]);

  // Read before the disclosure: a chip whose board stops resolving has no card to keep open.
  const { closeCard, handleOpenChange, isCardOpen, navigateFromCard } = useWorkspaceAgentPreviewCardDisclosure({
    focusNavigationOrigin: focusTrigger,
    hasSubject: !!board,
    onCardOpen: handleCardOpen,
  });

  useEffect(() => {
    if (error && !isWorkspaceAgentPillBoardUnresolvableError(error)) {
      reportPillBoardErrorOnce(boardId, error);
    }
  }, [boardId, error]);

  // The route needs the title, and the pill persists only `boardId`, so opening waits on the board.
  const handleOpenBoard = useCallback(() => {
    if (!board) {
      return;
    }

    navigateFromCard(() => openBoardPill(board));
  }, [board, openBoardPill, navigateFromCard]);

  let chip = <WorkspaceAgentPendingPill label="Board" />;

  if (board) {
    // `||`, not `??`: an empty-string cover would render `<img src="">` and suppress the fallback.
    const coverImageUrl = board.coverImage?.url || board.thumbnails?.[0] || null;

    chip = (
      <WorkspaceAgentReferencePill
        card={
          <WorkspaceAgentBoardPreviewCard
            closeButton={<WorkspaceAgentPreviewCardCloseButton onClose={closeCard} />}
            coverImageUrl={coverImageUrl}
            // The chip only draws a card once its board has resolved, so the card never waits here.
            isLoading={false}
            itemCount={board.directDescendantCount ?? null}
            location={getWorkspaceAgentBoardLocation(board)}
            onOpen={handleOpenBoard}
            referenceId={boardId}
            title={board.title}
          />
        }
        isOpen={isCardOpen}
        leadingVisual={
          <WorkspaceAgentPillThumbnail
            fallback={<BoardIcon aria-hidden data-testid="WORKSPACE_AGENT_MESSAGE_BOARD_PILL_ICON" size="xs" />}
            testId="WORKSPACE_AGENT_MESSAGE_BOARD_PILL_COVER"
            url={coverImageUrl}
          />
        }
        onOpenChange={handleOpenChange}
        surface="history"
        testId="WORKSPACE_AGENT_MESSAGE_BOARD_PILL"
        title={board.title}
        triggerRef={triggerRef}
        triggerTestId="WORKSPACE_AGENT_MESSAGE_BOARD_PILL_TRIGGER"
      />
    );
  } else if (error && !isLoading) {
    // Transient failures have exhausted their retry by now, so both kinds settle as unavailable.
    chip = <WorkspaceAgentUnavailablePill kind="board" referenceId={boardId} />;
  }

  // The observer needs a node, and every branch above is an atom with no ref to hand out.
  return (
    <span className="inline-flex max-w-full items-center align-middle leading-none" ref={ref}>
      {chip}
    </span>
  );
});

PrivateWorkspaceAgentBoardPillChip.displayName = 'PrivateWorkspaceAgentBoardPillChip';
